import { Injectable, NgZone } from '@angular/core';

/**
 * Habilita el arrastre de los diálogos grandes de licitación (los marcados con la clase
 * `sym-draggable-dialog` en su `styleClass`) mediante un ícono de agarre propio en el header,
 * en vez de depender de la propiedad `draggable` de PrimeNG.
 *
 * Motivo: `draggable: true` de PrimeNG resultó inconsistente entre diálogos con la misma
 * configuración (funcionaba en unos, no en otros, sin causa raíz identificada) y, al arrastrar
 * desde cualquier punto del header, seleccionaba texto en vez de mover la ventana. Un ícono
 * dedicado con arrastre implementado acá evita ambos problemas y no depende de esa propiedad.
 */
@Injectable({ providedIn: 'root' })
export class DraggableDialogService {
  private static readonly DIALOG_SELECTOR = '.sym-draggable-dialog';
  private static readonly HEADER_SELECTOR = '.p-dialog-header';
  private static readonly HANDLE_CLASS = 'sym-dialog-drag-handle';
  private static readonly DRAGGING_BODY_CLASS = 'sym-dialog-dragging';

  private observer?: MutationObserver;

  constructor(private zone: NgZone) {
    // Todo esto es manipulación directa del DOM fuera de los templates de Angular:
    // se ejecuta afuera de la zona para no disparar detección de cambios en cada mousemove.
    this.zone.runOutsideAngular(() => this.start());
  }

  private start(): void {
    this.observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return;
          this.attachIfDraggableDialog(node);
          node
            .querySelectorAll?.(DraggableDialogService.DIALOG_SELECTOR)
            .forEach((el) => this.attachIfDraggableDialog(el as HTMLElement));
        });
      }
    });
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  private attachIfDraggableDialog(el: HTMLElement): void {
    if (!el.matches(DraggableDialogService.DIALOG_SELECTOR)) return;

    const header = el.querySelector<HTMLElement>(DraggableDialogService.HEADER_SELECTOR);
    if (!header || header.querySelector(`.${DraggableDialogService.HANDLE_CLASS}`)) return;

    const handle = this.createHandle();
    header.insertBefore(handle, header.firstChild);
    this.bindDrag(handle, el);
  }

  private createHandle(): HTMLButtonElement {
    const handle = document.createElement('button');
    handle.type = 'button';
    handle.className = DraggableDialogService.HANDLE_CLASS;
    handle.setAttribute('aria-label', 'Mover ventana');
    handle.title = 'Arrastrar para mover';
    handle.innerHTML = `
      <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
        <path d="M12 3v18M5 8l-2 4 2 4M19 8l2 4-2 4" />
      </svg>`;
    return handle;
  }

  private bindDrag(handle: HTMLElement, dialogEl: HTMLElement): void {
    let dragging = false;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;

    const onMouseDown = (event: MouseEvent) => {
      // Evita que el mousedown también dispare el `initDrag` nativo de PrimeNG en el
      // `.p-dialog-header` (el handle vive dentro de él) y que se compitan por la posición.
      event.preventDefault();
      event.stopPropagation();

      const rect = dialogEl.getBoundingClientRect();
      startX = event.clientX;
      startY = event.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      dragging = true;

      dialogEl.style.position = 'fixed';
      dialogEl.style.margin = '0';
      dialogEl.style.left = `${startLeft}px`;
      dialogEl.style.top = `${startTop}px`;

      document.body.classList.add(DraggableDialogService.DRAGGING_BODY_CLASS);
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    const onMouseMove = (event: MouseEvent) => {
      if (!dragging) return;

      const rect = dialogEl.getBoundingClientRect();
      const minVisible = 60;
      const maxLeft = window.innerWidth - minVisible;
      const maxTop = window.innerHeight - minVisible;

      let left = startLeft + (event.clientX - startX);
      let top = startTop + (event.clientY - startY);
      left = Math.min(Math.max(left, minVisible - rect.width), maxLeft);
      top = Math.min(Math.max(top, 0), maxTop);

      dialogEl.style.left = `${left}px`;
      dialogEl.style.top = `${top}px`;
    };

    const onMouseUp = () => {
      dragging = false;
      document.body.classList.remove(DraggableDialogService.DRAGGING_BODY_CLASS);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    handle.addEventListener('mousedown', onMouseDown);
  }
}
